import { clsx } from "clsx";
import { Link, NavLink } from "react-router-dom";

import DesktopProfileDropdown from "features/layout/components/DesktopProfileDropdown";
import NavItem from "features/layout/components/NavItem";

import DesktopWorkspaceSelector from "common/components/DesktopWorkspaceSelector";
import useWorkspaces from "common/datahooks/useWorkspaces";
import { getWhitelabelCustomization } from "common/helpers/utils";
import type { NavItemType } from "common/types";

import DebugPopup from "./DebugPopup";
import ExpandCollapseButton from "./ExpandCollapseButton";
import ExtraNavItems from "./ExtraNavItems";
import NavItemTooltipWrapper from "./NavItemTooltipWrapper";
import NotificationNavItem from "./NotificationNavItem";
import OnboardingNavItem from "./OnboardingNavItem";

interface DesktopNavProps {
  navItems: NavItemType[];
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
}

export default function DesktopNav({
  navItems,
  isExpanded,
  setIsExpanded,
}: DesktopNavProps) {
  const { workspaces } = useWorkspaces();
  const expandCollapseButtonClassName = "transition-[opacity]";

  return (
    <nav
      className={clsx(
        "ml-2 z-nav flex flex-col transition-[width] duration-300 h-full py-2 shrink-0",
        isExpanded ? "w-52" : "w-11",
      )}
    >
      {getWhitelabelCustomization() ? (
        <div className="flex flex-col mb-3 gap-y-1.5">
          <div className="flex items-center px-1 py-2.5 mt-2.5 overflow-hidden">
            <div className="flex-1 flex relative group">
              <Link to="home" className="flex items-center h-9">
                <img
                  key={isExpanded.toString()}
                  src={
                    isExpanded
                      ? getWhitelabelCustomization().logo
                      : getWhitelabelCustomization().favicon
                  }
                  alt=""
                  className={clsx(
                    "transition-[padding,opacity] max-h-full",
                    isExpanded ? "px-4" : "group-hover:opacity-0 mr-auto",
                  )}
                />
              </Link>
              <div
                className={clsx(
                  "flex items-center justify-center absolute inset-0",
                  isExpanded && "pointer-events-none",
                )}
              >
                <ExpandCollapseButton
                  className={clsx(
                    expandCollapseButtonClassName,
                    "opacity-0 size-full items-center justify-center transition-opacity",
                    !isExpanded && "group-hover:opacity-100",
                  )}
                  isExpanded={isExpanded}
                  onClick={() => setIsExpanded(!isExpanded)}
                />
              </div>
            </div>
            <ExpandCollapseButton
              className={clsx(
                expandCollapseButtonClassName,
                !isExpanded && "hidden",
              )}
              isExpanded={isExpanded}
              onClick={() => setIsExpanded(!isExpanded)}
            />
          </div>
          {workspaces.length > 1 && (
            <div className="px-1 flex">
              <DesktopWorkspaceSelector isExpanded={isExpanded} />
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col px-1 mt-3 gap-y-1.5 items-start">
          <div className="flex gap-x-1.5 flex-1 items-center w-full">
            <DesktopWorkspaceSelector isExpanded={isExpanded} />
            <ExpandCollapseButton
              className={clsx(
                expandCollapseButtonClassName,
                isExpanded ? "delay-300" : "opacity-0 pointer-events-none",
              )}
              isExpanded={isExpanded}
              onClick={() => setIsExpanded(!isExpanded)}
            />
          </div>
          <ExpandCollapseButton
            className={clsx(
              expandCollapseButtonClassName,
              "ml-2.5",
              isExpanded ? "opacity-0 pointer-events-none" : "delay-300",
            )}
            onClick={() => setIsExpanded(!isExpanded)}
            isExpanded={isExpanded}
          />
        </div>
      )}
      <div className="flex flex-col gap-y-1 px-1 py-1.5">
        {navItems.map(({ path, icon, label, notificationsCount }) => {
          const navItemComponent = (
            <NavLink
              data-intercom-target={`nav-link-${path}`}
              data-cy={`nav-link-${path}`}
              key={path}
              to={path}
            >
              {({ isActive }) => {
                const commonProps = {
                  isActive,
                  icon,
                  isExpanded,
                  children: label,
                };
                if (path === "onboarding") {
                  return <OnboardingNavItem {...commonProps} />;
                }
                if (notificationsCount) {
                  return (
                    <NotificationNavItem
                      {...commonProps}
                      notificationsCount={notificationsCount}
                    />
                  );
                }
                return <NavItem {...commonProps} />;
              }}
            </NavLink>
          );

          return (
            <NavItemTooltipWrapper
              key={path}
              label={label}
              navItemComponent={navItemComponent}
              isExpanded={isExpanded}
            />
          );
        })}
      </div>
      <div className="mt-auto flex gap-y-1 flex-col px-1">
        {import.meta.env.MODE !== "production" && (
          <DebugPopup isExpanded={isExpanded} />
        )}
        <ExtraNavItems isExpanded={isExpanded} />
      </div>
      <DesktopProfileDropdown isExpanded={isExpanded} />
    </nav>
  );
}
